import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { PRODUCTS } from "@/lib/products";
import ThemeToggle from "@/components/ThemeToggle";
import LangSwitcher from "@/components/LangSwitcher";
import SanaaMark from "@/components/SanaaMark";
import { ChevronDown, LogOut, Menu, X } from "lucide-react";

export default function MarketingHeader() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const [appsOpen, setAppsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { to: "/#products", label: t("nav.products") },
    { to: "/#pricing", label: t("nav.pricing") },
    { to: "/#investors", label: t("nav.investors") },
  ];

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/70 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-5 py-4">
        <Link to="/" data-testid="logo-home" className="flex items-center gap-2">
          <SanaaMark size={26} />
          <span className="font-display text-base tracking-tight">Sanaa AI</span>
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-sm">
          {navLinks.map((l) => (
            <a
              key={l.to}
              href={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              {l.label}
            </a>
          ))}
          <div className="relative">
            <button
              data-testid="app-switcher-btn"
              onClick={() => setAppsOpen((o) => !o)}
              className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              Suite <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {appsOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setAppsOpen(false)} />
                <div className="absolute end-0 mt-3 w-72 rounded-sm border border-border bg-popover/95 backdrop-blur-xl p-1.5 shadow-2xl z-50">
                  {PRODUCTS.map((p) => (
                    <Link
                      key={p.key}
                      to={user ? p.route : "/auth"}
                      onClick={() => setAppsOpen(false)}
                      data-testid={`app-link-${p.key}`}
                      className="flex items-start gap-3 rounded-sm px-3 py-2.5 hover:bg-accent group"
                    >
                      <div
                        className="mt-0.5 h-8 w-8 shrink-0 rounded-sm"
                        style={{ background: p.gradient }}
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground">{p.name}</div>
                        <div className="text-xs text-muted-foreground line-clamp-1">vs {p.disrupt}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        </nav>

        <div className="flex items-center gap-2">
          <LangSwitcher className="hidden sm:block" />
          <ThemeToggle className="hidden sm:inline-flex" />
          {user ? (
            <>
              <button
                onClick={() => nav("/app")}
                data-testid="header-launch-app"
                className="hidden sm:inline-flex h-9 items-center rounded-sm bg-foreground px-3.5 text-sm font-medium text-background hover:opacity-90"
              >
                {t("nav.launch")}
              </button>
              <button
                onClick={() => {
                  logout();
                  nav("/");
                }}
                data-testid="header-logout"
                aria-label="Logout"
                className="hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-sm border border-border/60 bg-card/40 hover:bg-card"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </>
          ) : (
            <button
              onClick={() => nav("/auth")}
              data-testid="header-signin"
              className="hidden sm:inline-flex h-9 items-center rounded-sm bg-foreground px-3.5 text-sm font-medium text-background hover:opacity-90"
            >
              {t("nav.signin")}
            </button>
          )}
          <button
            onClick={() => setMobileOpen((o) => !o)}
            data-testid="mobile-menu-btn"
            aria-label="Menu"
            className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-sm border border-border/60 bg-card/40"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="md:hidden border-t border-border/60 bg-background/95 backdrop-blur-xl">
          <div className="mx-auto max-w-7xl px-5 py-4 grid gap-2">
            {navLinks.map((l) => (
              <a key={l.to} href={l.to} className="py-2 text-sm" onClick={() => setMobileOpen(false)}>
                {l.label}
              </a>
            ))}
            <div className="flex items-center gap-2 pt-2 border-t border-border/40">
              <LangSwitcher />
              <ThemeToggle />
              {user ? (
                <button
                  onClick={() => nav("/app")}
                  className="ms-auto h-9 px-3 rounded-sm bg-foreground text-background text-sm"
                >
                  {t("nav.launch")}
                </button>
              ) : (
                <button
                  onClick={() => nav("/auth")}
                  className="ms-auto h-9 px-3 rounded-sm bg-foreground text-background text-sm"
                >
                  {t("nav.signin")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
